import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Spin, message, notification, Button, DatePicker } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import locale from 'antd/lib/date-picker/locale/es_ES';
import { Bar } from 'react-chartjs-2';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from 'chart.js';
import moment from 'moment';
import { getWorkspaceUsageStatistics } from '../../API/services/workspaceStatisticsService';
import { t } from '../../i18n/i18n';
import './UsageStatistics.scss';
import '../../_variables.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController
);

const { RangePicker } = DatePicker;

const UsageStatistics = () => {
  const currentWorkspace = useSelector((state) => state.auth.currentWorkspace, shallowEqual);
  const [dateRange, setDateRange] = useState([
    moment().subtract(3, 'months').startOf('month'),
    moment(),
  ]);
  const [selectedMonths, setSelectedMonths] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  const dateRangeKey = useMemo(
    () =>
      dateRange?.[0] && dateRange?.[1]
        ? `${moment(dateRange[0]).format('YYYY-MM-DD')}_${moment(dateRange[1]).format(
            'YYYY-MM-DD'
          )}`
        : null,
    [dateRange]
  );

  useEffect(() => {
    if (!currentWorkspace || !dateRange?.[0] || !dateRange?.[1]) return;

    if (moment(dateRange[0]).isAfter(moment(dateRange[1]), 'day')) {
      message.error(t('USAGE_STATISTICS_ERROR_INVALID_DATE_RANGE'));
      return;
    }

    const fetchStatistics = async () => {
      if (isMountedRef.current) {
        setLoading(true);
        setStatistics(null);
      }
      try {
        const formattedStartDate = moment(dateRange[0]).format('YYYY-MM-DD');
        const formattedEndDate = moment(dateRange[1]).format('YYYY-MM-DD');

        const data = await getWorkspaceUsageStatistics(
          currentWorkspace,
          formattedStartDate,
          formattedEndDate
        );

        if (isMountedRef.current) {
          if (data && (data.summary || data.monthlyData)) {
            setStatistics(data);
          } else {
            setStatistics(null);
            message.warning(t('USAGE_STATISTICS_ERROR_NO_DATA'));
          }
        }
      } catch (error) {
        if (isMountedRef.current) {
          notification.error({
            message: t('USAGE_STATISTICS_ERROR_LOADING'),
            placement: 'topRight',
          });
          setStatistics(null);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchStatistics();

    return () => {};
  }, [currentWorkspace, dateRangeKey]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const generateMonthsData = (start, end) => {
    const months = [];
    const current = moment(start).startOf('month');
    const endMoment = moment(end).endOf('month');

    while (current.isSameOrBefore(endMoment) && months.length < 12) {
      months.push({
        month: current.format('YYYY-MM'),
        label: current.format('MMM'),
        fullLabel: current.format('MMMM YYYY'),
        isCurrentMonth: current.isSame(moment(), 'month'),
      });
      current.add(1, 'month');
    }

    return months;
  };

  const monthsData = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return [];
    return generateMonthsData(moment(dateRange[0]), moment(dateRange[1]));
  }, [dateRangeKey]);

  const monthlyDataMap = useMemo(() => {
    const map = {};
    if (statistics?.monthlyData && Array.isArray(statistics.monthlyData)) {
      statistics.monthlyData.forEach((item) => {
        map[item.month] = {
          invited: item.candidatesInvited || 0,
          completed: item.candidatesCompleted || 0,
        };
      });
    }
    return map;
  }, [statistics?.monthlyData]);

  const invitedValues = useMemo(
    () => monthsData.map((m) => monthlyDataMap[m.month]?.invited || 0),
    [monthsData, monthlyDataMap]
  );

  const completedValues = useMemo(
    () => monthsData.map((m) => monthlyDataMap[m.month]?.completed || 0),
    [monthsData, monthlyDataMap]
  );

  const percentageValues = useMemo(
    () =>
      monthsData.map((m) => {
        const invited = monthlyDataMap[m.month]?.invited || 0;
        const completed = monthlyDataMap[m.month]?.completed || 0;
        return invited > 0 ? parseFloat(((completed / invited) * 100).toFixed(1)) : 0;
      }),
    [monthsData, monthlyDataMap]
  );

  const chartData = useMemo(
    () => ({
      labels: monthsData.map((m) => {
        if (m.isCurrentMonth) {
          return `${m.label}\n${t('USAGE_STATISTICS_CURRENT_MONTH_LABEL')}`;
        }
        return m.label;
      }),
      datasets: [
        {
          label: t('USAGE_STATISTICS_CANDIDATES_INVITED'),
          data: invitedValues,
          backgroundColor: '#90CAF9',
          borderRadius: 8,
          maxBarThickness: 40,
          yAxisID: 'y',
          type: 'bar',
          order: 2,
        },
        {
          label: t('USAGE_STATISTICS_CANDIDATES_COMPLETED'),
          data: completedValues,
          backgroundColor: '#4987be',
          borderRadius: 8,
          maxBarThickness: 40,
          yAxisID: 'y',
          type: 'bar',
          order: 2,
        },
        {
          label: t('USAGE_STATISTICS_COMPLETION_PERCENTAGE'),
          data: percentageValues,
          borderColor: '#FF9800',
          backgroundColor: '#FF9800',
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.3,
          yAxisID: 'y1',
          type: 'line',
          order: 1,
        },
      ],
    }),
    [monthsData, invitedValues, completedValues, percentageValues]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.dataset.type === 'line') {
                label += context.parsed.y + '%';
              } else {
                label += context.parsed.y;
              }
              return label;
            },
          },
        },
      },
      scales: {
        y: {
          type: 'linear',
          display: true,
          position: 'left',
          beginAtZero: true,
          ticks: {
            stepSize: 10,
          },
          grid: {
            drawBorder: false,
          },
          title: {
            display: true,
            text: t('USAGE_STATISTICS_CANDIDATES'),
            font: {
              size: 11,
            },
          },
        },
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          beginAtZero: true,
          max: 100,
          ticks: {
            stepSize: 20,
            callback: function (value) {
              return value + '%';
            },
          },
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: t('USAGE_STATISTICS_COMPLETION_PERCENTAGE'),
            font: {
              size: 11,
            },
          },
        },
        x: {
          grid: {
            display: false,
          },
        },
      },
    }),
    []
  );

  const totalCandidatosInvitados = statistics?.summary?.candidatesInvited ?? 0;
  const totalCandidatosRindieron = statistics?.summary?.candidatesCompleted ?? 0;
  const porcentajeRindieron = statistics?.summary?.completionPercentage ?? 0;
  const evaluacionesActivas = statistics?.summary?.activeEvaluations ?? 0;
  const jobsPostActivos = statistics?.summary?.activeJobPosts ?? 0;

  const onChange = (dates) => {
    if (!dates) {
      setDateRange(null);
      setSelectedMonths(null);
      return;
    }

    if (dates[0] && dates[1]) {
      if (moment(dates[0]).isAfter(moment(dates[1]), 'day')) {
        message.error(t('USAGE_STATISTICS_ERROR_INVALID_DATE_RANGE'));
        return;
      }
      setDateRange(dates);
      setSelectedMonths(null);
    } else if (dates[0] && !dateRange) {
      setDateRange(dates);
      setSelectedMonths(null);
    }
  };

  const handleQuickSelect = (months) => {
    const today = moment();
    const startDate = today
      .clone()
      .subtract(months - 1, 'months')
      .startOf('month');
    const endDate = today.clone().endOf('day');

    const newRange = [startDate, endDate];
    setDateRange(newRange);
    setSelectedMonths(months);
  };

  if (loading) {
    return (
      <Card className="usage-card" bordered={false}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (
    !statistics ||
    (!statistics.summary && (!statistics.monthlyData || statistics.monthlyData.length === 0))
  ) {
    return (
      <Card className="usage-card" bordered={false}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>{t('USAGE_STATISTICS_ERROR_NO_DATA')}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card
      id="USAGE_STATISTICS"
      title={
        <div className="card-title-with-filter">
          <span>{t('USAGE_STATISTICS_TITLE')}</span>
          <div className="date-controls">
            <div className="quick-selects">
              <Button
                size="small"
                type={selectedMonths === 4 ? 'primary' : 'default'}
                onClick={() => handleQuickSelect(4)}
              >
                4M
              </Button>
              <Button
                size="small"
                type={selectedMonths === 5 ? 'primary' : 'default'}
                onClick={() => handleQuickSelect(5)}
              >
                5M
              </Button>
              <Button
                size="small"
                type={selectedMonths === 6 ? 'primary' : 'default'}
                onClick={() => handleQuickSelect(6)}
              >
                6M
              </Button>
            </div>
            <RangePicker
              value={dateRange}
              onChange={onChange}
              format="DD/MM/YYYY"
              locale={locale}
              className="react-datepicker-input"
              disabledDate={(current) => current && current > moment().endOf('day')}
            />
          </div>
        </div>
      }
      className="usage-card"
      bordered={false}
    >
      <div className="usage-content">
        <div className="stats-sidebar">
          <div className="stat-box">
            <div className="stat-label">{t('USAGE_STATISTICS_CANDIDATES_INVITED')}</div>
            <div className="stat-value">{totalCandidatosInvitados}</div>
          </div>

          <div className="stat-box">
            <div className="stat-label">{t('USAGE_STATISTICS_CANDIDATES_COMPLETED')}</div>
            <div className="stat-value">{totalCandidatosRindieron}</div>
          </div>

          <div className="stat-box">
            <div className="stat-label">{t('USAGE_STATISTICS_JOBS_POST_ACTIVE')}</div>
            <div className="stat-value">{jobsPostActivos}</div>
          </div>
        </div>

        <div className="chart-section">
          <h3 className="chart-title">{t('USAGE_STATISTICS_CANDIDATES_COMPLETED')}</h3>
          <div className="chart-container">
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UsageStatistics;
